import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  TableSortLabel,
  alpha
} from '@mui/material';

// Icons
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  CreditCard as CardIcon,
  AccountBalance as BankIcon
} from '@mui/icons-material';

// Services
import { paymentService } from '../../services/paymentService';
import { formatDateTimeUniversal } from '../../utils/formatters';

// Constants
import { PAYMENT_METHODS, STRIPE_METHODS, DEPOSIT_STATUSES } from '../../constants/paymentConstants';

// Configuration pour pagination Load More simple
const ITEMS_PER_PAGE = 5; // Nombre d'éléments par page

const OwnerDepositsHistoryInfiniteSimple = forwardRef(({ filters = {}, onError, disableAutoLoad = false }, ref) => {
  const { t } = useTranslation(['financial', 'common']);
  
  // Refs pour éviter les dépendances cycliques
  const loadingRef = useRef(false);
  const currentPageRef = useRef(1); // Ref pour suivre la page courante
  const currentFiltersRef = useRef({}); // Ref pour les filtres actuels

  // États principaux simplifiés
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  
  // États pour les devises et méthodes de paiement dynamiques
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  
  // États pour les filtres et le tri
  const [localFilters, setLocalFilters] = useState({
    status: '',
    paymentMethod: '',
    paymentStatus: '', // Nouveau filtre pour payment_status
    currency: '',
    dateFrom: '',
    dateTo: '',
    ...filters
  });
  
  // Initialiser le ref des filtres
  useEffect(() => {
    currentFiltersRef.current = localFilters;
  }, []);
  
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // 🔄 Chargement d'une page de dépôts avec Load More
  const loadMoreDeposits = useCallback(async (reset = false) => {
    // Utiliser useRef pour éviter les dépendances cycliques
    if (loadingRef.current) {
      console.log('⚠️ Already loading, skipping request');
      return;
    }
    
    const pageToLoad = reset ? 1 : currentPageRef.current + 1;
    console.log(`🔧 Loading OWNER DEPOSITS PAGE: ${pageToLoad}, size ${ITEMS_PER_PAGE}, reset: ${reset}`);
    console.log('🔍 DEBUG Filters applied:', currentFiltersRef.current);
    
    loadingRef.current = true;
    setLoading(true);
    
    if (reset) {
      setError(null);
      setDeposits([]);
      setCurrentPage(1);
      currentPageRef.current = 1;
    }
    
    try {
      // Construire les paramètres avec filtrage propre
      const requestParams = {
        page: pageToLoad,
        limit: ITEMS_PER_PAGE,
        type: 'deposit',
        include_deposits: true,
        deposits_only: true,
        component: 'OwnerDepositsHistoryOptimized',
        sortBy,
        sortDirection
      };

      // Ajouter les filtres seulement s'ils ont des valeurs
      if (currentFiltersRef.current.status) requestParams.status = currentFiltersRef.current.status;
      if (currentFiltersRef.current.paymentMethod) requestParams.method = currentFiltersRef.current.paymentMethod; // Mapper paymentMethod -> method
      if (currentFiltersRef.current.paymentStatus) requestParams.payment_status = currentFiltersRef.current.paymentStatus; // Nouveau filtre
      if (currentFiltersRef.current.currency) requestParams.currency = currentFiltersRef.current.currency;
      if (currentFiltersRef.current.dateFrom) requestParams.start_date = currentFiltersRef.current.dateFrom;
      if (currentFiltersRef.current.dateTo) requestParams.end_date = currentFiltersRef.current.dateTo;

      console.log('🔍 DEBUG Request params:', requestParams);

      const response = await paymentService.getOwnerDepositsHistory(requestParams);
      
      console.log('📦 Response from API:', response);
      console.log('🔍 DEBUG Response data details:', {
        historyLength: response.history?.length,
        firstItems: response.history?.slice(0, 2).map(item => ({
          id: item.id,
          amount: item.amount,
          currency: item.currency,
          status: item.status,
          paymentMethod: item.payment_method,
          paymentStatus: item.payment_status
        }))
      });
      
      if (!response?.success) throw new Error(response?.error || 'Failed to load owner deposits');
      
      const allPayments = response.history || response.data?.history || response.data?.payments || [];
      
      // Traitement optimisé des données pour OWNER
      const processedDeposits = allPayments.map((item, index) => {
        const deposit = item.deposit || item;
        
        return {
          ...deposit,
          id: deposit.id || deposit._id || `owner-deposit-${pageToLoad}-${index}`,
          amount: deposit.amount || item.total_amount || 0,
          date: deposit.created_at || item.created_at || deposit.date || deposit.createdAt,
          status: deposit.status || 'held',
          paymentMethod: deposit.payment_method || 'unknown',
          paymentStatus: deposit.payment_status || item.payment_status || 'unknown',
          currency: item.currency || deposit.currency || 'EUR',
          reservation_id: item.reservation_id || deposit.reservation_id,
          reservation_title: item.reservation_title,
          reservation_status: item.reservation_status,
          listing_id: item.listing_id || deposit.listing_id,
          listing_title: item.listing_title || deposit.listing_title,
          renter_name: item.renter_name || deposit.renter_name
        };
      }).filter(deposit => deposit.id);
      
      const pagination = response.pagination || response.data?.pagination || {};
      const hasMoreData = Boolean(
        pagination.has_next_page || 
        pagination.hasNext || 
        (processedDeposits.length === ITEMS_PER_PAGE && pageToLoad < (pagination.total_pages || 999))
      );
      
      console.log(`✅ Loaded ${processedDeposits.length} owner deposits from page ${pageToLoad}`, {
        hasMoreData,
        totalPages: pagination.total_pages,
        currentPage: pagination.current_page,
        totalItems: pagination.total_count || pagination.total
      });
      
      // Mise à jour des états
      if (reset) {
        setDeposits(processedDeposits);
        setCurrentPage(1);
        currentPageRef.current = 1;
      } else {
        setDeposits(prev => [...prev, ...processedDeposits]);
        setCurrentPage(pageToLoad);
        currentPageRef.current = pageToLoad; // Synchroniser le ref
      }
      
      setHasMorePages(hasMoreData);
      setTotalItems(pagination.total_count || pagination.total || processedDeposits.length);
      
      // Mettre à jour les devises et méthodes disponibles
      if (processedDeposits.length > 0) {
        const currencies = [...new Set(processedDeposits.map(d => d.currency).filter(Boolean))];
        const methods = [...new Set(processedDeposits.map(d => d.paymentMethod).filter(Boolean))];
        
        setAvailableCurrencies(prev => [...new Set([...prev, ...currencies])]);
        setAvailablePaymentMethods(prev => [...new Set([...prev, ...methods])]);
      }
      
    } catch (error) {
      console.error('❌ Error loading owner deposits:', error);
      setError(error.message || 'Erreur lors du chargement des dépôts');
      if (onError) onError(error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
      setInitialLoading(false);
    }
  }, []); // Retirer les dépendances car on utilise maintenant les refs

  // 🚀 Initialisation au chargement du composant - UNE SEULE FOIS
  useEffect(() => {
    if (!disableAutoLoad) {
      console.log('🎯 Initialisation OwnerDepositsHistoryInfiniteSimple');
      loadMoreDeposits(true); // reset = true pour charger la première page
    }
  }, []); // AUCUNE dépendance pour éviter les re-montages

  // 🔄 Rechargement lors du changement de filtres
  const handleFiltersChange = useCallback(() => {
    console.log('🔄 Filtres changés, rechargement des données');
    loadMoreDeposits(true);
  }, []);

  // 🎯 Bouton Load More
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMorePages && !loadingRef.current) {
      console.log('🔄 Load More clicked, current page:', currentPage);
      console.log('🔍 Current filters at Load More:', currentFiltersRef.current);
      loadMoreDeposits(false); // reset = false pour charger la page suivante
    }
  }, []); // Retirer les dépendances

  // 🎛️ Gestion des filtres locaux
  const updateLocalFilters = useCallback((newFilters) => {
    console.log('🔄 Updating filters:', newFilters);
    setLocalFilters(prev => {
      const updated = { ...prev, ...newFilters };
      console.log('🔍 Updated filters state:', updated);
      return updated;
    });
  }, []);

  // 🔄 Effect pour recharger quand les filtres changent
  useEffect(() => {
    // Mettre à jour le ref des filtres
    currentFiltersRef.current = localFilters;
    
    if (!initialLoading) {
      console.log('🔄 Filters changed, reloading data with new filters:', localFilters);
      loadMoreDeposits(true);
    }
  }, [localFilters]);

  // 🔄 Gestion du tri
  const handleSort = useCallback((newSortBy) => {
    const newDirection = sortBy === newSortBy && sortDirection === 'desc' ? 'asc' : 'desc';
    console.log('🔄 Updating sort:', { newSortBy, newDirection });
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  }, [sortBy, sortDirection]);

  // 🎯 Interface pour les refs
  useImperativeHandle(ref, () => ({
    refresh: () => loadMoreDeposits(true),
    loadMore: () => loadMoreDeposits(false),
    clearFilters: () => {
      setLocalFilters({
        status: '',
        paymentMethod: '',
        paymentStatus: '', // Ajouter le nouveau filtre
        currency: '',
        dateFrom: '',
        dateTo: ''
      });
      handleFiltersChange();
    }
  }), []);

  // 🎨 Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case DEPOSIT_STATUSES.PENDING: return 'default';
      case DEPOSIT_STATUSES.PAID: return 'info';
      case DEPOSIT_STATUSES.HELD: return 'warning';
      case DEPOSIT_STATUSES.RELEASED: return 'success';
      case DEPOSIT_STATUSES.REFUNDED: return 'error';
      case DEPOSIT_STATUSES.WITHHELD: return 'error';
      case DEPOSIT_STATUSES.PARTIALLY_CAPTURED: return 'info';
      case DEPOSIT_STATUSES.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  // 🎨 Fonction pour obtenir l'icône de la méthode de paiement
  const getPaymentMethodIcon = (method) => {
    if (STRIPE_METHODS.includes(method)) {
      return <CardIcon sx={{ fontSize: 16 }} />;
    }
    return <BankIcon sx={{ fontSize: 16 }} />;
  };

  // 🎨 Formatage de la date
  const formatDate = (dateString) => {
    return formatDateTimeUniversal(dateString);
  };

  // 🎨 Rendu des filtres
  const renderFilters = () => (
    <Collapse in={isFilterOpen}>
      <Box sx={{ p: 2, backgroundColor: alpha('#000', 0.02), borderRadius: 1, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('financial:activeFilters.status')}</InputLabel>
              <Select
                value={localFilters.status}
                label={t('financial:activeFilters.status')}
                onChange={(e) => updateLocalFilters({ status: e.target.value })}
              >
                <MenuItem value="">{t('financial:status.all')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.PENDING}>{t('financial:status.pending')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.PAID}>{t('financial:status.paid')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.HELD}>{t('financial:status.held')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.RELEASED}>{t('financial:status.released')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.REFUNDED}>{t('financial:status.refunded')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.WITHHELD}>{t('financial:status.withheld')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.PARTIALLY_CAPTURED}>{t('financial:status.partially_captured')}</MenuItem>
                <MenuItem value={DEPOSIT_STATUSES.CANCELLED}>{t('financial:status.cancelled')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('financial:payment_method')}</InputLabel>
              <Select
                value={localFilters.paymentMethod}
                label={t('financial:payment_method')}
                onChange={(e) => updateLocalFilters({ paymentMethod: e.target.value })}
              >
                <MenuItem value="">{t('financial:status.all')}</MenuItem>
                {availablePaymentMethods.map(method => (
                  <MenuItem key={method} value={method}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getPaymentMethodIcon(method)}
                      {t(`financial:payment_methods.${method}`, method)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('financial:transaction')}</InputLabel>
              <Select
                value={localFilters.paymentStatus}
                label={t('financial:transaction')}
                onChange={(e) => updateLocalFilters({ paymentStatus: e.target.value })}
              >
                <MenuItem value="">{t('financial:status.all')}</MenuItem>
                <MenuItem value="pending">{t('financial:payment_status.pending')}</MenuItem>
                <MenuItem value="completed">{t('financial:payment_status.completed')}</MenuItem>
                <MenuItem value="failed">{t('financial:payment_status.failed')}</MenuItem>
                <MenuItem value="partial">{t('financial:payment_status.partial')}</MenuItem>
                <MenuItem value="refunded">{t('financial:payment_status.refunded')}</MenuItem>
                <MenuItem value="cancelled">{t('financial:payment_status.cancelled')}</MenuItem>
                </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('financial:currency')}</InputLabel>
              <Select
                value={localFilters.currency}
                label={t('financial:currency')}
                onChange={(e) => updateLocalFilters({ currency: e.target.value })}
              >
                <MenuItem value="">{t('financial:status.all')}</MenuItem>
                {availableCurrencies.map(currency => (
                  <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('financial:date_from')}
              value={localFilters.dateFrom}
              onChange={(e) => updateLocalFilters({ dateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('financial:date_to')}
              value={localFilters.dateTo}
              onChange={(e) => updateLocalFilters({ dateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Box>
    </Collapse>
  );

  // 📊 Rendu du composant principal
  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('financial:loading_owner_deposits')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header avec titre et contrôles */}
      <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center', 
                mb: 3,
                background: 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, #ff9800 30%, #f57c00 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}>
        <Typography variant="h6" component="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
          {t('financial:owner_deposits_history')}
          {totalItems > 0 && (
            <Typography component="span" variant="body2" sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.8)' }}>
              ({deposits.length}/{totalItems})
            </Typography>
          )}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            sx={{ 
              color: 'white',
              backgroundColor: isFilterOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
            size="small"
          >
            <FilterIcon />
          </IconButton>
          
          <IconButton
            onClick={() => loadMoreDeposits(true)}
            disabled={loading}
            sx={{ 
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filtres */}
      {renderFilters()}

      {/* Affichage des erreurs */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tableau des dépôts */}
      {deposits.length > 0 ? (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'date'}
                    direction={sortBy === 'date' ? sortDirection : 'desc'}
                    onClick={() => handleSort('date')}
                  >
                    {t('financial:date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'amount'}
                    direction={sortBy === 'amount' ? sortDirection : 'desc'}
                    onClick={() => handleSort('amount')}
                  >
                    {t('financial:amount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortDirection : 'desc'}
                    onClick={() => handleSort('status')}
                  >
                    {t('financial:status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t('financial:transaction')}</TableCell>
                <TableCell>{t('financial:payment_method')}</TableCell>
                <TableCell>{t('financial:listing')}</TableCell>
                <TableCell>{t('financial:renter_name')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deposits.map((deposit) => (
                <TableRow key={deposit.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(deposit.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {deposit.amount} {deposit.currency}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`financial:status.${deposit.status}`)}
                      color={getStatusColor(deposit.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`financial:payment_status.${deposit.paymentStatus}`, deposit.paymentStatus)}
                      color={deposit.paymentStatus === 'completed' ? 'success' : deposit.paymentStatus === 'pending' ? 'warning' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getPaymentMethodIcon(deposit.paymentMethod)}
                      <Typography variant="body2">
                        {t(`financial:payment_methods.${deposit.paymentMethod}`, deposit.paymentMethod)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {deposit.listing_title || deposit.listing_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {deposit.renter_name || 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('financial:no_owner_deposits_found')}
          </Typography>
        </Paper>
      )}

      {/* Bouton Load More */}
      {hasMorePages && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
          >
            {loading ? t('common:loading') : t('financial:load_more')}
          </Button>
        </Box>
      )}

      {/* Information de pagination */}
      {deposits.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('financial:showing_items', { count: deposits.length, total: totalItems })}
            {!hasMorePages && totalItems > 0 && (
              <Typography component="span" sx={{ ml: 1 }}>
                • {t('financial:all_items_loaded')}
              </Typography>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

OwnerDepositsHistoryInfiniteSimple.displayName = 'OwnerDepositsHistoryInfiniteSimple';

export default OwnerDepositsHistoryInfiniteSimple;
